import { makeFloat, useCurrency, useGetFreeSpins } from 'react-easyrocket';

type GetUserBalanceInfoReturnTypes = {
  isLoading: boolean;
  showFsInfo: boolean;
  realBalance: string;
  bonusBalance: string;
  fs: number;
  fsBet: string;
  fsWithDayLimit: string;
  fsBalance: string;
  totalBalance: string;
  currencyName: string;
  currencyIcon: string;
};

type GetUserBalanceInfoProps = {
  enableFreeSpinsRefetch?: boolean;
  disableBalanceRefetch?: boolean;
};

const REFETCH_INTERVAL = 3000;
const DEFAULT_FREE_SPINS_REFETCH_INTERVAL = 60000;

/**
 * Хук для получения макс развернутой инфы по балансу пользователя
 * ### Параметры
 * - `enableFreeSpinsRefetch`: булевое для изменения __refetchInterval__ запроса __useGetFreeSpins__ (в игре надо запрашивать каждые 3сек, в остальных случаях каждую минуту )
 * - `disableBalanceRefetch`: булевое для изменения __refetchInterval__ запроса __useCurrency__
 * ### Возвращаемые значения
 * - `isLoading`: булевое показывающее состояние получения данных
 * - `showFsInfo`: булевое говорящее надо ли показывать блок с инфо по FS в выпадашке с иформацией о балансе в игре (если нет FS то не показываем)
 * - `realBalance`: чистый баланс пользователя без бонусов и FS
 * - `bonusBalance`: бонусный баланс пользователя
 * - `fs`: кол-во FS доступных пользователю
 * - `fsBet`: ставка FS
 * - `fsWithDayLimit`: строка формата 12/20 показывающая кол-во доступных пользователю FS если есть dayLimit
 * - `fsBalance`: сумма FS
 * - `totalBalance`: суммарный баланс (regular + bonus + FS)
 * - `currencyName`: название активной валюты 'EUR' | 'USD' и т.д.
 * - `currencyIcon`: иконка валюты
 */

export const useGetUserBalanceInfo = ({
  enableFreeSpinsRefetch = false,
  disableBalanceRefetch = false
}: GetUserBalanceInfoProps = {}): GetUserBalanceInfoReturnTypes => {
  const currency = useCurrency({
    optionsForGetShortBalance: {
      refetchInterval: !disableBalanceRefetch && REFETCH_INTERVAL,
      target: 'intervalBalanceUpdate'
    }
  });
  const { currencyIcon, shortBalance, currencyName } = currency;
  const { freeSpins } = useGetFreeSpins({
    refetchInterval: enableFreeSpinsRefetch ? REFETCH_INTERVAL : DEFAULT_FREE_SPINS_REFETCH_INTERVAL
  });

  const { numberDay, number, dayLimit, betAmount } = freeSpins || {};

  const fs = dayLimit ? numberDay : number;

  const realBalanceValue = shortBalance?.balanceMain;
  const bonusBalanceValue = shortBalance?.bonusCasino;
  const fsBalanceValue = shortBalance?.bonusFreespins;
  const totalBalanceValue = shortBalance?.balance;

  const realBalance = `${currencyIcon} ${makeFloat(realBalanceValue)}`;
  const bonusBalance = `${currencyIcon} ${makeFloat(bonusBalanceValue)}`;
  const fsBet = `${currencyIcon} ${makeFloat(betAmount)}`;
  const fsWithDayLimit = dayLimit ? `${numberDay}/${dayLimit}` : `${number}`;
  const fsBalance = `${currencyIcon} ${makeFloat(fsBalanceValue)}`;
  const totalBalance = totalBalanceValue
    ? `${currencyIcon} ${makeFloat(totalBalanceValue)}`
    : `${currencyIcon} 0.00`;

  const isLoading = shortBalance === undefined || freeSpins === undefined;

  const showFsInfo = fs !== 0;

  return {
    isLoading,
    realBalance,
    bonusBalance,
    fs,
    showFsInfo,
    fsBet,
    fsWithDayLimit,
    fsBalance,
    totalBalance,
    currencyName,
    currencyIcon
  };
};
